/**
 * EmailGateContext — mounts the "add your email before we can email
 * you" modal once at the Fitting root and exposes a promise-returning
 * `requireVerifiedEmail()` for action buttons to guard themselves.
 *
 * Provider responsibilities:
 *   1. Watches the current customer's email state.
 *   2. Auto-opens the modal ONCE per browser session when a phone-only
 *      customer is loaded (the "signup popup" ask). Dismissal is
 *      remembered in sessionStorage so a page navigation doesn't
 *      re-slam the modal on top of every subsequent screen.
 *   3. Provides `requireVerifiedEmail()` which resolves to `true` when
 *      the customer already has a verified real email, otherwise opens
 *      the modal + resolves `false`. Callers on the button side use it
 *      as `if (!(await requireVerifiedEmail())) return;` before firing
 *      any action that would trigger an email downstream.
 *
 * Non-goals:
 *   * Blocking every screen — the modal isn't a wall. It fires on
 *     first-mount and per action-attempt; the golfer can still browse.
 *   * Enforcing verification on the server — that's the belt-and-
 *     braces layer in the 4 send-* edge functions
 *     (see supabase/functions/_shared/requireVerifiedEmail.ts).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuthStore } from '../store/authStore';
import { needsEmailForAction } from '../lib/emailGate';
import { isPlaceholderEmail } from '../lib/golferEmail';
import { supabase } from '../lib/supabase';
import { GolferEmailRequiredModal } from '../components/golfer/GolferEmailRequiredModal';

interface EmailGateApi {
  /** Resolves `true` when the current customer has a real verified
   *  email, otherwise opens the modal + resolves `false`. Never
   *  rejects — callers do a boolean check. */
  requireVerifiedEmail: () => Promise<boolean>;
}

const EmailGateContext = createContext<EmailGateApi | null>(null);

const AUTO_PROMPT_KEY = 'golfer_email_auto_prompt_shown';

export function EmailGateProvider({ children }: { children: ReactNode }) {
  const customer = useAuthStore((s) => s.customer);
  const professional = useAuthStore((s) => s.professional);
  const [modalOpen, setModalOpen] = useState(false);
  // Track whether we've already auto-shown the modal this session so
  // it doesn't reopen on every route change once dismissed.
  const autoShownRef = useRef<boolean>(
    typeof window !== 'undefined' &&
      window.sessionStorage.getItem(AUTO_PROMPT_KEY) === 'true',
  );

  // First-mount auto-prompt: when a phone-only customer lands, open the
  // modal once. Dismissal is sticky via sessionStorage — the golfer can
  // still hit gated actions and see the modal re-open on demand.
  useEffect(() => {
    if (autoShownRef.current) return;
    if (!customer) return;
    const gate = needsEmailForAction(customer);
    if (!gate.block) return;
    autoShownRef.current = true;
    try {
      window.sessionStorage.setItem(AUTO_PROMPT_KEY, 'true');
    } catch {
      // sessionStorage can throw in private mode — non-fatal.
    }
    setModalOpen(true);
  }, [customer]);

  const requireVerifiedEmail = useCallback(async () => {
    // 1. Customer row loaded — usual path. Apply the gate directly.
    if (customer) {
      const gate = needsEmailForAction(customer);
      if (!gate.block) return true;
      setModalOpen(true);
      return false;
    }
    // 2. Pro session — pass through. Pros use `professionals.email`
    //    with its own verification flow; the golfer gate doesn't
    //    apply. Bugbot round-3 MEDIUM on #361 — needsEmailForAction
    //    can't tell "pro" apart from "golfer with un-hydrated
    //    customer row", so we differentiate here where the auth
    //    store IS the source of truth.
    if (professional) return true;
    // 3. Neither in store yet — could be a golfer mid-hydration OR a
    //    dual-hat pro whose professional row loaded first. Fall back
    //    to the auth user's email: a synthetic
    //    `<phone>@phone.golfauth.local` is the reliable phone-only
    //    signal. Block on placeholder, pass through when it's a real
    //    address (a golfer whose customer row is late to load will
    //    still get re-gated by the auto-open effect above when the
    //    hydration completes).
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return true;
      if (isPlaceholderEmail(user.email)) {
        setModalOpen(true);
        return false;
      }
    } catch {
      // Session read failure — don't hard-block; the surrounding
      // auth flow will surface a login prompt if the session is
      // actually gone.
    }
    return true;
  }, [customer, professional]);

  const handleClose = useCallback(() => setModalOpen(false), []);

  const api = useMemo<EmailGateApi>(
    () => ({ requireVerifiedEmail }),
    [requireVerifiedEmail],
  );

  return (
    <EmailGateContext.Provider value={api}>
      {children}
      <GolferEmailRequiredModal open={modalOpen} onClose={handleClose} />
    </EmailGateContext.Provider>
  );
}

export function useEmailGate(): EmailGateApi {
  const ctx = useContext(EmailGateContext);
  if (!ctx) {
    // Defensive default so a page rendered outside the provider (e.g.
    // the Pro-side admin surface where phone-only golfers never land)
    // doesn't crash. Treats every request as allowed — matches the
    // "no gate needed" semantic of the Pro app.
    return {
      requireVerifiedEmail: async () => true,
    };
  }
  return ctx;
}
